import React, { useRef, useState } from 'react'
import { FormInput, FormTextIput } from '../input/input.component'
import { CustomButton } from '../button/button.component'
import { ContactH1, DivButton, FormDiv, StyleP, Div } from './contact.styles'
import { AnimatePresence } from 'framer-motion'
// Lib Functions
import { announceAnalyticsInteraction } from '../../lib/analytics'

function ContactForm() {
  const [formSent, setFormSent] = useState(false)
  const [formError, setFormError] = useState('')
  const [isSending, setIsSending] = useState(false)
  const formStarted = useRef(false)
  const submissionPending = useRef(false)
  const [type, setType] = useState({
    checkin: 'text',
    date: 'text',
  })
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    checkIn: '',
    message: '',
    website: '',
  })
  const { name, phone, email, date, checkIn, message, website } = formData

  const handleChange = (event) => {
    if (!formStarted.current) {
      formStarted.current = true
      announceAnalyticsInteraction('form_started', {
        form_id: 'contact-form',
        form_type: 'contact',
      })
    }
    const { name, value } = event.target
    setFormData({ ...formData, [name]: value })
  }

  const onSubmit = async () => {
    if (submissionPending.current) return

    const missingFields = [name, email, message].some((value) => !value.trim())
    const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    if (missingFields || !emailIsValid) {
      setFormError('Please enter your name, a valid email address, and a message.')
      announceAnalyticsInteraction('form_failed', {
        form_id: 'contact-form',
        form_type: 'contact',
        error_code: 'validation_failed',
      })
      return
    }

    submissionPending.current = true
    setIsSending(true)
    setFormError('')
    announceAnalyticsInteraction('form_submitted', {
      form_id: 'contact-form',
      form_type: 'contact',
    })
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) {
        const error = new Error('contact_form_failed')
        error.code = result.error
        throw error
      }
      setFormData({
        name: '',
        phone: '',
        email: '',
        date: '',
        checkIn: '',
        message: '',
        website: '',
      })
      setFormSent(true)
      announceAnalyticsInteraction('form_succeeded', {
        form_id: 'contact-form',
        form_type: 'contact',
      })
    } catch (error) {
      setFormError(
        error.code === 'rate_limited'
          ? 'Too many messages have been sent. Please wait a few minutes and try again.'
          : 'Your message could not be sent. Please try again.'
      )
      announceAnalyticsInteraction('form_failed', {
        form_id: 'contact-form',
        form_type: 'contact',
        error_code: 'submission_failed',
      })
    } finally {
      submissionPending.current = false
      setIsSending(false)
    }
  }

  return (
    <Div>
      <AnimatePresence>
        {!formSent && (
          <FormDiv
            initial={{ x: 0, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{
              duration: 1,
            }}
            exit={{ x: -300, opacity: 0 }}
          >
            <StyleP>Complete this form and we will contact you.</StyleP>
            <label htmlFor="name" hidden={true}>
              Fill in your name:
            </label>
            <FormInput
              label={'Enter Name'}
              onChange={handleChange}
              value={name}
              name={'name'}
              required
              maxLength={120}
            />
            <label htmlFor="phone" hidden={true}>
              Fill in your phone number:
            </label>
            <FormInput
              label={'Enter Phone Number'}
              onChange={handleChange}
              value={phone}
              name={'phone'}
              maxLength={50}
            />
            <label htmlFor="phone" hidden={true}>
              Fill in your email address:
            </label>
            <FormInput
              type={'email'}
              onChange={handleChange}
              value={email}
              name={'email'}
              label={'Email'}
              required
              maxLength={254}
            />
            <label htmlFor="message" hidden={true}>
              Add your message here:
            </label>
            <FormInput
              type={type.date}
              onFocus={() => setType({ ...type, date: 'date' })}
              onChange={handleChange}
              value={date}
              name={'date'}
              label={'Date'}
              maxLength={40}
            />
            <label htmlFor="date" hidden={true}>
              Add your message here:
            </label>
            {/* <FormInput type={type.checkin} onFocus={() => setType({ ...type, checkin: 'date' })} onChange={handleChange} value={checkIn} name={'checkIn'} label={'Check In'} />
						<label htmlFor='checkIn' hidden={true}>
							Add your message here:
						</label> */}
            <FormTextIput
              multiline="true"
              onChange={handleChange}
              value={message}
              name={'message'}
              label={'Message'}
              required
              maxLength={5000}
            />
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px' }}
            >
              <label htmlFor="website">Leave this field empty</label>
              <input
                id="website"
                name="website"
                value={website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            {formError && <StyleP role="alert">{formError}</StyleP>}
            <DivButton>
              <CustomButton
                name={'Send message now'}
                onClick={onSubmit}
                disabled={isSending}
                aria-busy={isSending}
                data-analytics-cta-id="contact-submit"
                data-analytics-cta-type="form_submit"
                data-analytics-cta-label="Send Message"
                data-analytics-placement-id="contact-form"
              >
                {isSending ? 'Sending…' : 'Send Message'}
              </CustomButton>
            </DivButton>
          </FormDiv>
        )}
      </AnimatePresence>
      {formSent && (
        <AnimatePresence>
          <FormDiv
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{
              delay: 1,
              duration: 1,
            }}
          >
            <StyleP>Your message has been sent.</StyleP>
            <br></br>
            <br></br>
            <StyleP>Thank you for taking the time to contact us.</StyleP>
            <br></br> <br></br>
            <StyleP>We will be in contact shortly.</StyleP>
          </FormDiv>
        </AnimatePresence>
      )}
    </Div>
  )
}

export { ContactForm }
